/**
 * This file handles everything that is needed to manage contestant objects in the frontend.
 *
 *
 * If there is a call to get a contestant that the store cant find, try and add it to the store from the database.
 *
 *
 */

import { ContestantBase, IntArray, RelayTeamContestant, SearchRequest } from '@/objects/generated';
import { defineStore, storeToRefs } from 'pinia';
import { useEventStore } from './eventStore';
import { BaseObjDao, ContestantDao, RankingCategoryDao } from '@/dao';
import { computed, onUnmounted, readonly, ref, watch } from 'vue';
import { CapitalizeWords } from '@/utilities/utils';
import { type SelectLine, type SelectLineValueType } from '@/objects';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import WellKnown from '@/objects/generated/static/WellKnown';
import { arrayDiff, arrayIntersect, CreateRegexpSearchPatternForSearchType } from '@/utilities';
import { useEventCommonStore } from './eventCommonStore';
import type {
    Bib,
    ContestantId,
    RaceContestantId,
    RaceId,
    RelayTeamContestantId,
    ParticipantId,
    CategoryId,
    GroupId,
} from '@/utilities/typealiases';
import { useCommonStore } from './commonStore';
import { findAvailableCategoriesForGenderAndBirthdayInCurrentEvent } from '@/utilities/categoryutils';
import { useCommonStoreRef } from './commonStore';
import { useEventCommonStoreRef } from './eventCommonStore';
import { resolveablePromise } from '@/utilities/promiseutils';

export type ContestantValues = 'Contestants' | 'RelayContestants';

type DisplayName = 'fullwithbib' | 'full' | 'short' | 'bibonly';

export const useContestantStore = defineStore('contestantstore', () => {
    const ContestantStatusInfo = {
        visible: [
            WellKnown.CONTESTANTSTATUS_REGISTERED,
            WellKnown.CONTESTANTSTATUS_LICENSEREGISTERED,
            WellKnown.CONTESTANTSTATUS_UNKOWNATHLETE,
            WellKnown.CONTESTANTSTATUS_CAROUSELSIGNUP,
            WellKnown.CONTESTANTSTATUS_LICENSEAPPROVED,
            WellKnown.CONTESTANTSTATUS_UNREGISTEREDANDLICENSEAPPROVED,
            WellKnown.CONTESTANTSTATUS_UNREGISTEREDANDLICENSEDECLINED,
            WellKnown.CONTESTANTSTATUS_REFUNDED_DONATED,
            WellKnown.CONTESTANTSTATUS_REFUNDED_REQUESTEDREFUND,
            WellKnown.CONTESTANTSTATUS_REFUNDED_REQUESTEDTRANSFER,
            WellKnown.CONTESTANTSTATUS_REFUNDED_REFUNDED,
            WellKnown.CONTESTANTSTATUS_REFUNDED_TRANSFERRED,
            WellKnown.CONTESTANTSTATUS_REFUNDED_DONATEDMANUALLY,
        ],
    };

    // Helper property to determine if the contestants have been loaded from the backend
    const isLoaded = ref(false);

    // Helper to see if inital load are done
    let isInitialLoadDone = resolveablePromise<boolean>();

    const evStore = useEventStore();
    const { eventID, initpromise } = storeToRefs(evStore);

    const Common = useCommonStoreRef();
    const EventCommon = useEventCommonStoreRef();

    // the loaded contestants
    const contestants = ref<ContestantBase[]>([]);

    // the visible contestants (status in list )
    const visibleContestants = ref<ContestantBase[]>([]);

    // Contains all the relay contestants
    const relayContestants = ref<Map<ContestantId, RelayTeamContestant[]>>(new Map());

    // Contains the individual IDs for the relay contestants
    const relayContestantIds: RelayTeamContestantId[] = [];

    // Contains a map of all the rankingCaegory combinations
    const rankingCategoriesMap = ref<Map<RaceContestantId, number[]>>(new Map());

    /**
     * Loader for the relay contestants
     */
    const relayContestantLoader = ref<LoaderWithReset<RelayTeamContestant[]>>(
        new LoaderWithReset<RelayTeamContestant[]>({
            defaultValue: [],
            loaderFunction: async (): Promise<RelayTeamContestant[]> => {
                const changes = await BaseObjDao.Load(RelayTeamContestant, eventID.value, relayContestantIds);

                const changesId = changes.map((c) => c.ID);

                // We merge the changes with the current relay contestants
                const oldRelayConstestants = relayContestantLoader.value.Value?.filter((rcc) => !changesId.includes(rcc.ID)) ?? [];

                const data = changes.concat(oldRelayConstestants);

                // we udate the relay contestants
                relayContestants.value = new Map<ContestantId, RelayTeamContestant[]>(
                    data.map((rcc) => [rcc.ContestantID, data.filter((rcc2) => rcc2.ContestantID === rcc.ContestantID)]),
                );

                return data;
            },
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    const ContestantsFullNameAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const fnSort = (a: ContestantBase): string => {
            if (a.RelayTeamName && a.RelayTeamName.length > 0) {
                return a.RelayTeamName;
            }
            return a.Surname ?? 'XXX';
        };

        const fnName = (c: ContestantBase): string => {
            if (c.RelayTeamName && c.RelayTeamName.length > 0) {
                return c.RelayTeamName;
            }
            return CapitalizeWords(`${c.Firstname} ${c.Surname}`);
        };

        // Sort by surname
        const sorted = [...contestants.value].sort((a, b) => fnSort(a).localeCompare(fnSort(b)));
        const result = new Map<SelectLineValueType, SelectLine>();
        sorted.forEach((c) => {
            result.set(c.ID, { value: c.ID, text: fnName(c) });
        });
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the full name of the contestant
     */
    const ContestantFullNameWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...Array.from(ContestantsFullNameAsSelectLine.value.entries()),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the firstname of the contestant
     */
    const ContestantFirstnameWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) => [c.ID, { value: c.ID, text: CapitalizeWords(`${c.Firstname}`) }] as [SelectLineValueType, SelectLine],
            ),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the surname of the contestant
     */
    const ContestantSurnameWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) => [c.ID, { value: c.ID, text: CapitalizeWords(`${c.Surname}`) }] as [SelectLineValueType, SelectLine],
            ),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the team name of the contestant
     */
    const ContestantTeamWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) =>
                    [c.ID, { value: c.ID, text: Common.ClubsMap.value.get(c.TeamID ?? 0)?.Name ?? '' }] as [
                        SelectLineValueType,
                        SelectLine,
                    ],
            ),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the club name of the contestant
     */
    const ContestantClubWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) =>
                    [c.ID, { value: c.ID, text: Common.ClubsMap.value.get(c.ClubID ?? 0)?.Name ?? '' }] as [
                        SelectLineValueType,
                        SelectLine,
                    ],
            ),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the category name of the contestant
     */
    const ContestantCategoryWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) =>
                    [c.ID, { value: c.ID, text: EventCommon.Categories.value.find((t) => t.ID === c.CategoryID)?.Name ?? '' }] as [
                        SelectLineValueType,
                        SelectLine,
                    ],
            ),
        ]);
        return result;
    });

    /**
     * This is a computed value that returns a list of selectlines with the region name of the contestant
     */
    const ContestantRegionWithEmptyAsSelectLine = computed<Map<SelectLineValueType, SelectLine>>(() => {
        const result = new Map<SelectLineValueType, SelectLine>([
            [null, { value: null, text: '' }],
            ...contestants.value.map(
                (c) =>
                    [c.ID, { value: c.ID, text: Common.RegionsMap.value.get(c.RegionID ?? 0)?.Name ?? '' }] as [
                        SelectLineValueType,
                        SelectLine,
                    ],
            ),
        ]);
        return result;
    });

    // The store should have a map of contestant objects, where the key is the contestant id.
    const contestantMap = ref<Map<ContestantId, ContestantBase>>(new Map());

    // Contains a index of raceContestantId -> contestantId
    const raceContestantMap = ref<Map<RaceContestantId, ContestantId>>(new Map());

    // Contains a map of participant id to contestant object
    const participantContestantMap = ref<Map<ParticipantId, ContestantId>>(new Map());

    // Contains a lookup from Bib to ContestantId
    const bibRaceContestantMap = ref<Map<Bib, ContestantId>>(new Map());

    /**
     * Contains a map of how many contestants is in each race
     */
    const visibleRaceContestantCount = ref<Map<RaceId, number>>(new Map());

    // A cache of contestants by category
    const categoryContestants = new Map<CategoryId, Map<ContestantId, ContestantBase>>();

    /**
     * A set of subscriptions to be called when we have signalr events for contestants
     */
    const signalrSubscriptions = new Set<(contestants: ContestantBase[]) => void>();

    /**
     * The callback that is called when we have signalr events for contestants
     * @param eventId
     * @param DtoType
     * @param id
     * @returns
     */
    const contestantBaseCallback = async (values: ContestantBase[]) => {
        // TODO: Change this to just run the updateBaseContestants with the contestants we get from signalr. This need to be updated in CacheManager first since IsoneID is not mapped there yet
        // So for now we rerun the load from the database.
        // updateBaseContestants(values);

        const ids = values.map((c) => c.ID);

        if (ids.length === 0) {
            return;
        }

        await loadContestants(ids);

        // We need to get all the ones in values that are NOT in Contestants and add them to a deletedItemList
        const deletedItemList = values.filter((c) => c.EntityState == 'Deleted');

        // TODO: Why do we not update the baseContestants if we dont have any deleted items?
        if (deletedItemList.length > 0) {
            updateBaseContestants(deletedItemList);
        }

        // Notify subscribers
        signalrSubscriptions.forEach((callback) => {
            callback(values);
        });
    };

    /**
     * Loads a list of contestants from the database, or ALL if no ids are provided.
     * @param ids
     */
    const loadContestants = async (ids: number[]) => {
        // Fetch all contestants for the event and add them to the store
        const _contestants = (await ContestantDao.GetBaseContestants(eventID.value, IntArray.FromJsValues({ Values: ids }))) ?? [];

        // We also load the RankingCategories. This should probably be done by ID and via SignalR, but for now we leave it here, since it is VERY little data
        const _rankingCategories = (await RankingCategoryDao.GetRankingCategoriesForEvent(eventID.value)) ?? [];

        // We need to group this by RaceContestantId and create a Map of RaceContestantId to RankingCategoryId[] and then we need to loop through the contestants and add the RankingCategoryIds to the contestant objects. We do this because the ranking categories are needed to determine the available categories for a contestant, and we want to have this in the store for easy access.
        rankingCategoriesMap.value = new Map<RaceContestantId, number[]>(
            _rankingCategories.reduce((acc, rc) => {
                const raceContestantId = rc.RaceContestantID;
                const existing = acc.get(raceContestantId) ?? [];
                existing.push(rc.CategoryID);
                acc.set(raceContestantId, existing);
                return acc;
            }, new Map<RaceContestantId, number[]>()),
        );

        isLoaded.value = true;

        updateBaseContestants(_contestants);
    };

    /**
     * Get the contestants from the store
     * @param ids
     * @returns
     */
    const getContestants = (ids?: number[]): ContestantBase[] => {
        // if ids is not set or empty we return all contestants
        if (!ids || ids.length === 0) {
            return Array.from(contestantMap.value.values());
        }
        return (ids.map((id) => contestantMap.value.get(id)).filter((c) => c != null) as ContestantBase[]) ?? [];
    };

    /**
     * Get a contestant by its id
     * @param id
     * @returns
     */
    const getContestantById = (id: number): ContestantBase | undefined => contestantMap.value.get(id);

    /**
     * Finds categories that matches the contestant
     * @param contestantId The contestant to find matching categories for
     * @param raceId Optionally filter to a specific race
     * @returns An array of CategoryIds that are available for the contestant
     */
    const getAvailableCategoriesForContestant = (contestantId: ContestantId, raceId?: RaceId, groupId?: GroupId) => {
        const contestant = contestantMap.value.get(contestantId);
        const eventCommonStore = useEventCommonStore();
        const commonStore = useCommonStore();

        contestant?.CategoryID;

        if (eventCommonStore.Event == null || commonStore.SportForEvent == null || eventCommonStore.Categories.length === 0) {
            return [];
        }

        return findAvailableCategoriesForGenderAndBirthdayInCurrentEvent({
            birthday: contestant?.Birthday ?? undefined,
            age: contestant?.EventAge ?? undefined,
            gender: contestant?.Gender ?? undefined,
            alwaysIncludeCategory: contestant?.CategoryID ?? undefined,
            raceId: raceId,
            groupId: groupId,
        });
    };

    /**
     * Finds categories available for all the racecontestants provided.
     *
     * Meaning we only return categories that match ALL the racecontestants provided.
     * @param raceContestantIds The racecontestants to find categories for
     * @returns
     */
    const getAvailableCategoriesForRaceContestants = (raceContestantIds: number[]): CategoryId[] => {
        let categories: CategoryId[] | null = null;

        raceContestantIds.forEach((rcid) => {
            if (!raceContestantMap.value.has(rcid)) {
                return;
            }
            const contestant = contestantMap.value.get(raceContestantMap.value.get(rcid)!);

            if (!contestant) {
                return;
            }

            const raceContestant = contestant.RaceContestants.find((rc) => rc.ID === rcid);

            const contestantCategories = getAvailableCategoriesForContestant(
                contestant.ID,
                raceContestant?.RaceID,
                raceContestant?.GroupID,
            );
            categories = arrayIntersect(categories ?? contestantCategories, contestantCategories);
        });
        return categories ?? [];
    };

    /**
     * This is a waitable function that makes sure we have loaded all the contestants for the event
     * @param dataTypeToEnsureIsLoaded -  undused but this will let guibuilder call this (TODO: Should we make an interface for this?)
     */
    const ensureLoaded = async (dataTypeToEnsureIsLoaded: ContestantValues[]) => {
        const promises = [];

        // Do we ever need to load more things? Maybe Bibs??
        if (dataTypeToEnsureIsLoaded.includes('Contestants')) {
            // Wait for the initial load to be done, we do not init loading here as that is done by watching the eventID

            promises.push(isInitialLoadDone.promise);
        }

        if (dataTypeToEnsureIsLoaded.includes('RelayContestants')) {
            promises.push(relayContestantLoader.value.Load());
        }

        await Promise.all(promises);
    };

    /**
     * Return the display name for a contestant object
     *
     * For the display types:
     *
     * - fullwithbib: "Bib. Firstname Surname"
     *
     * - full: "Firstname Surname"
     *
     * - short: "F. Surname"
     *
     * - bibonly: "Bib"
     * @param contestant The contestant object to format the name from
     * @param displaytype How to format the name
     * @param maxlength The maximum length of the name
     */
    const getDisplayNameForContestantObject = (
        contestant: ContestantBase,
        displaytype: DisplayName = 'fullwithbib',
        maxlength?: number,
    ) => {
        switch (displaytype) {
            case 'full':
                return teamOrName(contestant, false, maxlength);
            case 'short': {
                return teamOrName(contestant, true, maxlength);
            }
            case 'bibonly':
                return contestant.Bib != null ? contestant.Bib.toString() : '';

            case 'fullwithbib':
                return (contestant.Bib != null ? `${contestant.Bib}. ` : '') + teamOrName(contestant, false, maxlength);
        }
    };

    /**
     * Format the name
     * @param contestant
     * @param short
     * @param maxlength The maximum length of the name
     * @returns
     */
    const teamOrName = (contestant: ContestantBase, short: boolean, maxlength?: number) => {
        let name = '';
        if (contestant.RelayTeamName && contestant.RelayTeamName.length > 0) {
            return contestant.RelayTeamName;
        }

        if (short) {
            let firstname = contestant.Firstname ?? '';
            firstname = firstname.charAt(0).toUpperCase() + '.';
            name = `${firstname} ${contestant.Surname}`;
        } else {
            name = `${contestant.Firstname} ${contestant.Surname}`;

            // we need to check if the name is longer than maxlength and if so we need to shorten it. But we want to primary remove middle names and then if it is still too long we remove characters from the end of the name until it fits.
            if (maxlength && name.length > maxlength) {
                const nameParts = contestant.Firstname?.split(' ');
                if (nameParts && nameParts.length > 1) {
                    name = `${nameParts[0]} ${contestant.Surname}`;
                }
            }

            if (maxlength && name.length > maxlength) {
                return teamOrName(contestant, true);
            }
        }
        return CapitalizeWords(name);
    };

    /**
     * Return the display name for a contestant
     * For the display types:
     *
     * - fullwithbib: "Bib. Firstname Surname"
     *
     * - full: "Firstname Surname"
     *
     * - short: "F. Surname"
     *
     * - bibonly: "Bib"
     * @param raceContestantId The raceContestantId to use to lookup the contestant
     * @param displaytype How to format the name
     */
    const getDisplayName = (raceContestantId: number, displaytype: DisplayName = 'fullwithbib') => {
        // Get the contestant id from the map
        const contestantId = raceContestantMap.value.get(raceContestantId);

        if (!contestantId) {
            return '';
        }

        // Get the contestant from the map
        const contestant = contestantMap.value.get(contestantId);

        if (!contestant) {
            return '';
        }

        return getDisplayNameForContestantObject(contestant, displaytype);
    };

    /**
     * This will update the contestants in the store based on the participant ids provided.
     * @param participantIds
     * @returns
     */
    const updateContestantsByParticipantIds = async (participantIds: number[]) => {
        // This will load the contestants for the given participant ids and update the store
        if (participantIds.length === 0) {
            return;
        }
        const contestantsToUpdate = await ContestantDao.GetContestantsByParticipantIds(
            IntArray.FromJsValues({ Values: participantIds }),
            eventID.value,
        );

        updateBaseContestants(contestantsToUpdate);
    };

    /**
     * Internal helper function to update the contestant map and the raceContestantMap and participantContestantMap
     * @param contestantsToUpdate
     */
    const updateBaseContestants = (contestantsToUpdate: ContestantBase[]) => {
        if (contestantsToUpdate.length === 0) {
            return;
        }

        // We create a temporary map to hold the contestants to avoid multiple writes to the reactive map
        const _contestantsMap = contestantMap.value;
        const _raceContestantMap = raceContestantMap.value;

        const _participantContestantMap = participantContestantMap.value;
        const _bibRaceContestantMap = bibRaceContestantMap.value;

        const _contestants = new Map<ContestantId, ContestantBase>(contestants.value.map((c) => [c.ID, c]));

        const _visibleContestants = new Map<ContestantId, ContestantBase>(visibleContestants.value.map((c) => [c.ID, c]));

        // Update the contestant map with the new contestants
        contestantsToUpdate.forEach((contestant) => {
            // TODO: We need to RAnkingCategories... Should move from Complete to here.
            const contestantCategoryIds: number[] = [contestant.CategoryID];
            const oldContestant = _contestantsMap.get(contestant.ID);

            // On delete we just remove the contestant from all maps and lists
            if (contestant.EntityState === 'Deleted') {
                // We clean up all the maps and lists
                _contestantsMap.delete(contestant.ID);
                _participantContestantMap.delete(contestant.ParticipantID);

                if (contestant.Bib) {
                    _bibRaceContestantMap.delete(contestant.Bib ?? -1);
                }

                for (const rcId of oldContestant?.RaceContestants.map((rc) => rc.ID) ?? []) {
                    _raceContestantMap.delete(rcId);
                }

                // We remove the contestant from the contestants list
                _contestants.delete(contestant.ID);

                // We need to iterate the contestantCategories and remove them from the _categoryContestants if they exists
                for (const categoryId of contestantCategoryIds) {
                    const contestantList = categoryContestants.get(categoryId);

                    if (contestantList?.has(contestant.ID)) {
                        contestantList.delete(contestant.ID);
                    }
                }
            } else {
                // We need to update the contestant in the map

                _contestantsMap.set(contestant.ID, contestant);
                _participantContestantMap.set(contestant.ParticipantID, contestant.ID);

                // We should remove the old race contestants from the map if they are not in the new contestant
                // We need to loop through the map and check the value if it is the contestant and then remove it

                const raceContestantIdDiff = arrayDiff(
                    oldContestant?.RaceContestants.map((rc) => rc.ID) ?? [],
                    contestant.RaceContestants?.map((rc) => rc.ID) ?? [],
                );

                if (raceContestantIdDiff.length > 0) {
                    for (const rcId of raceContestantIdDiff) {
                        _raceContestantMap.delete(rcId);
                    }
                }

                if (contestant.Bib && contestant.RaceContestants && contestant.RaceContestants.length > 0) {
                    _bibRaceContestantMap.set(contestant.Bib, contestant.ID);
                }

                // Update the raceContestantMap and participantContestantMap
                contestant.RaceContestants?.forEach((raceContestant) => {
                    _raceContestantMap.set(raceContestant.ID, contestant.ID);

                    if (raceContestant.AlternativeBib) {
                        _bibRaceContestantMap.set(raceContestant.AlternativeBib, contestant.ID);
                    }
                });

                // Update the contestants list
                _contestants.set(contestant.ID, contestant);

                // and the visible
                if (ContestantStatusInfo.visible.includes(contestant.ContestantStatusID)) {
                    _visibleContestants.set(contestant.ID, contestant);
                } else {
                    _visibleContestants.delete(contestant.ID);
                }

                if (oldContestant && oldContestant?.CategoryID !== contestant.CategoryID) {
                    // Remove from old category
                    const oldCategoryList = categoryContestants.get(oldContestant.CategoryID);
                    if (oldCategoryList && oldCategoryList.has(contestant.ID)) {
                        oldCategoryList.delete(contestant.ID);
                    }
                }

                const newCategoryList = categoryContestants.get(contestant.CategoryID);

                if (!newCategoryList) {
                    categoryContestants.set(contestant.CategoryID, new Map<ContestantId, ContestantBase>([[contestant.ID, contestant]]));
                } else {
                    newCategoryList.set(contestant.ID, contestant);
                }
            }
        });

        // Update the reactive maps
        contestantMap.value = new Map(_contestantsMap);
        raceContestantMap.value = new Map(_raceContestantMap);
        participantContestantMap.value = new Map(_participantContestantMap);
        bibRaceContestantMap.value = new Map(_bibRaceContestantMap);

        // Update the reactive contestants list
        contestants.value = [..._contestants.values()];
        visibleContestants.value = [..._visibleContestants.values()];

        // We need to group all the RaceContestants by RaceID and then count them
        const _visibleRaceContestantsCount = new Map<RaceId, number>();
        for (const contestant of visibleContestants.value) {
            contestant.RaceContestants?.forEach((raceContestant) => {
                if (!_visibleRaceContestantsCount.has(raceContestant.RaceID)) {
                    _visibleRaceContestantsCount.set(raceContestant.RaceID, 0);
                }
                _visibleRaceContestantsCount.set(raceContestant.RaceID, _visibleRaceContestantsCount.get(raceContestant.RaceID)! + 1);
            });
        }
        visibleRaceContestantCount.value = _visibleRaceContestantsCount;
    };

    /**
     * Return the relay contestants, either all or for a specific team ( a team is defined by a contestant)
     * @param contestantId
     * @returns
     */
    const getRelayContestants = (contestantId?: number): RelayTeamContestant[] => {
        if (contestantId) {
            return relayContestants.value.get(contestantId) ?? [];
        }
        return Array.from(relayContestants.value.values()).flat();
    };

    let contestantsUpdatedUnsubscribeFunction: (() => Promise<void>) | null = null;

    const contestantsDeletedUnsubscribeFunction: (() => Promise<void>) | null = null;

    const subscribeToContestantsUpdated = (callback: (contestants: ContestantBase[]) => void) => {
        // Add the callback
        signalrSubscriptions.add(callback);

        // When we unmount we remove the callback
        return () => signalrSubscriptions.delete(callback);
    };

    /**
     * Find a contestant based on the bib
     */
    const getContestantByBib = (bib: number): ContestantBase | undefined => {
        // Get the contestant id from the map
        const contestantId = bibRaceContestantMap.value.get(bib);

        if (!contestantId) {
            return undefined;
        }

        // Get the contestant from the map
        return contestantMap.value.get(contestantId);
    };

    /**
     * Finds contestants by chip id
     *
     * Note: This uses .find to do a linear search, as opposed to looking up in a map. Use with care
     * @param chipId The chip we are looking for
     * @param chip Which chipfield to look in
     * @returns A ContestantBase object if we find it, undefined otherwise
     */
    const getContestantByChip = (chipId: string, chip: 'both' | 'chip1' | 'chip2' = 'both'): ContestantBase | undefined =>
        contestants.value.find((c) => {
            if (chip === 'both') {
                return c.Chip1 === chipId || c.Chip2 === chipId;
            } else if (chip === 'chip1') {
                return c.Chip1 === chipId;
            } else if (chip === 'chip2') {
                return c.Chip2 === chipId;
            }
        });

    /**
     * Search the contestants by a search request
     * @param SearchRequest
     * @returns A list of contestants that matches the search request
     */
    const searchContestants = (SearchRequest: SearchRequest): ContestantBase[] => {
        if (!SearchRequest.Query || SearchRequest.Query.trim() === '') {
            return [];
        }

        // If the query is an integer we asssume it is a bib search
        if (/^\d+$/.test(SearchRequest.Query)) {
            const result = getContestantByBib(parseInt(SearchRequest.Query));
            return result ? [result] : [];
        }

        // We find the contestant based on the firstname, surname or relayteam name
        const pattern = CreateRegexpSearchPatternForSearchType(SearchRequest.Query, SearchRequest.SearchType);

        return contestants.value.filter((c) => {
            if (pattern.test(c.Firstname ?? '')) {
                return true;
            }

            if (pattern.test(c.Surname ?? '')) {
                return true;
            }

            const eventTeamName = useEventCommonStore().EventTeamsAsSelectLine.get(c.EventTeamId)?.text ?? '';

            return pattern.test(eventTeamName);
        });
    };

    /**
     * Return a list of all the contestants in a category
     * @param categoryId The category to get contestants for
     * @param options Options for the query
     */
    const getCategoryContestants = (
        categoryId: number,
        options?: {
            /**
             * If true, only return contestants that has a status that is considered visible
             */
            visibleOnly?: boolean;
        },
    ): ContestantBase[] => {
        const contestants = categoryContestants.get(categoryId) ?? [];
        if (!options?.visibleOnly) {
            return Array.from(contestants.values());
        }
        return Array.from(contestants.values()).filter((c) => ContestantStatusInfo.visible.includes(c.ContestantStatusID));
    };

    /**
     * Reset the store and reload all contestants
     */
    const reset = () => {
        isInitialLoadDone = resolveablePromise<boolean>();
        contestants.value = [];
        visibleContestants.value = [];
        contestantMap.value = new Map();
        raceContestantMap.value = new Map();
        participantContestantMap.value = new Map();
        relayContestants.value = new Map();

        relayContestantLoader.value.Reset();
    };

    /**
     * Initialize the load of contestants on every event changed.
     * This will also unsubscribe from the old event and subscribe to the new contestant changes
     */
    watch(
        eventID,
        async (nv) => {
            if (nv && nv > 0) {
                reset();

                await loadContestants([]);

                isInitialLoadDone.resolver(true);

                if (contestantsUpdatedUnsubscribeFunction) {
                    await contestantsUpdatedUnsubscribeFunction();
                }

                // TODO: We need to check that the UtoverChanged also works for all contestants, not only for the events for the last 5 days.
                contestantsUpdatedUnsubscribeFunction = await signalrSubscribe.ContestantBase(
                    { EventID: eventID.value },
                    contestantBaseCallback,
                );

                // TODO: Add a subscription
            }
        },
        { immediate: true },
    );

    return {
        /**
         * Subscribe to contestant updates from signalr
         */
        SubscribeToContestantsUpdated: subscribeToContestantsUpdated,

        /**
         * Get the contestants from the store
         * @param ids
         * @returns
         */
        GetContestants: getContestants,

        /**
         * Get a contestant by its id
         */
        GetContestantById: getContestantById,

        /**
         * Search for contestants by a searchRequest
         * @param searchRequest
         * @returns
         */
        SearchContestants: searchContestants,

        /**
         * Update the contestants in the store based on the participant ids provided.
         * @param participantIds
         */
        UpdateContestantsByParticipantIds: updateContestantsByParticipantIds,

        /**
         * Get a contestant by one of its race contestant ids
         * @param id
         * @returns
         */
        GetContestantByRaceContestantId: (raceContestantId: number): ContestantBase | undefined => {
            // Get the contestant id from the map
            const contestantId = raceContestantMap.value.get(raceContestantId);

            if (!contestantId) {
                return undefined;
            }

            // Get the contestant from the map
            return contestantMap.value.get(contestantId);
        },

        /**
         * Get a contestant by its participant id
         * @param participantId
         * @returns
         */
        GetContestantByParticipantId: (participantId: number): ContestantBase | undefined => {
            // Get the contestant id from the map
            const contestantId = participantContestantMap.value.get(participantId);

            if (!contestantId) {
                return undefined;
            }

            // Get the contestant from the map
            return contestantMap.value.get(contestantId);
        },

        /**
         * Get the ranking categories for a race contestant
         * @param raceContestantId The ID of the race contestant
         * @returns An array of ranking category IDs
         */
        GetRankingCategoriesForRaceContestant: (raceContestantId: number): number[] => {
            return rankingCategoriesMap.value.get(raceContestantId) ?? [];
        },

        GetContestantByBib: getContestantByBib,

        GetContestantByChip: getContestantByChip,

        GetAvailableCategoriesForRaceContestants: getAvailableCategoriesForRaceContestants,

        /**
         * This is a waitable function that makes sure we have loaded all the contestants for the event
         * @param dataTypeToEnsureIsLoaded -  undused but this will let guibuilder call this (TODO: Should we make an interface for this?)
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Return the display name for a contestant
         * @param raceContestantId
         */
        GetDisplayName: getDisplayName,

        GetDisplayNameForContestantObject: getDisplayNameForContestantObject,

        /**
         * The list of all loaded contestants
         */
        Contestants: contestants,

        /**
         * The list of all visible contestants
         */
        VisibleContestants: visibleContestants,

        /**
         * This is set to true after the contestants have been loaded from the backend
         * @remark This is only set once.
         */
        IsLoaded: readonly(isLoaded),

        /**
         * The current count of contestants that are visible (status in list)
         */
        VisibleContestantCount: computed(() => {
            const start = performance.now();
            const result = visibleContestants.value.length;
            const end = performance.now();
            console.log(`[computed] VisibleContestantCount: ${(end - start).toFixed(2)}ms`);
            return result;
        }),

        /**
         * The contestant count grouped by race, only include the visible
         */
        VisibleRaceContestantCount: visibleRaceContestantCount,

        /**
         * Get a list of all the relay contestants or a list of relaycontestants for one team (contestant)
         */
        GetRelayContestants: getRelayContestants,

        /**
         * Return a list of all the contestants in a category
         */
        GetCategoryContestants: getCategoryContestants,

        /**
         * Load all contestants
         */
        RelayContestants: computed(() => relayContestantLoader.value.Value),

        /**
         * Reload all or some relay contestants
         * @param ids
         */
        ReloadRelayContestants: async (ids?: number[]) => {
            // TODO: We should find a better way of providing the ids
            if (ids !== undefined && ids.length > 0) {
                for (const id of ids) {
                    relayContestantIds.push(id);
                }
            }
            await relayContestantLoader.value.Reload();
            relayContestantIds.length = 0;
        },

        /**
         * Reload all or some contestants
         * @param ids
         */
        ReloadContestants: async (ids?: number[]) => {
            contestants.value = [];
            await loadContestants(ids ?? []);
        },

        /**
         * A list of all contestants full name as selectlines
         */
        ContestantsFullNameAsSelectLine: ContestantsFullNameAsSelectLine,

        /**
         * A list of all contestants full name as selectlines with empty as the first line
         */
        ContestantFullNameWithEmptyAsSelectLine,

        /**
         * A list of all contestants firstname as selectlines with empty as the first line
         */
        ContestantFirstnameWithEmptyAsSelectLine,

        /**
         * A list of all contestants surname as selectlines with empty as the first line
         */
        ContestantSurnameWithEmptyAsSelectLine,

        /**
         * A list of all contestants team name as selectlines with empty as the first line
         */
        ContestantTeamWithEmptyAsSelectLine,

        /**
         * A list of all contestants club name as selectlines with empty as the first line
         */
        ContestantClubWithEmptyAsSelectLine,

        /**
         * A list of all contestants category name as selectlines with empty as the first line
         */
        ContestantCategoryWithEmptyAsSelectLine,

        /**
         * A list of all contestants region name as selectlines with empty as the first line
         */
        ContestantRegionWithEmptyAsSelectLine,

        /**
         * The list of all visible contestant statuses
         */
        VisibleContestantStatuses: ContestantStatusInfo.visible,
    };
});

/**
 * A reactive reference to the common store.
 * @returns
 */
export const useContestantStoreRef = () => storeToRefs(useContestantStore());

// helper composable to handle lifetime of a subscription to the contestant updates from signalr. This will automatically unsubscribe when the component is unmounted.
export function useContestantUpdateSubscription(callback: (contestants: ContestantBase[]) => void) {
    const store = useContestantStore();

    const cleanup = store.SubscribeToContestantsUpdated(callback);

    onUnmounted(() => {
        cleanup();
    });
}
